/**
 * CompareView — SyncSketch-style two-column review for Layout & Animation.
 *
 * Founder spec (May 23 2026): Replace the cluttered tile-grid for Layout
 * & Animation with a side-by-side comparison reviewer.
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Artist: @Jaydeep ▾   Shot: HNG_SH001 ▾   ◀ Prev   Next ▶     │
 *   ├─────────────────────────────────┬────────────────────────────┤
 *   │ CBA (Animatic + VO)             │ Animator playblast         │
 *   │ HNG_SH001_CBA_v001              │ HNG_SH001_LAY_v003 ▾       │
 *   │ ┌────────────────────────────┐  │ ┌──────────────────────┐   │
 *   │ │  ◀ Reference video    ▶    │  │ │  ◀ Latest version  ▶ │   │
 *   │ └────────────────────────────┘  │ └──────────────────────┘   │
 *   ├─────────────────────────────────┴────────────────────────────┤
 *   │ ▶ Master Play  ▮▮ Pause  ⟲ Sync to 0  Hot keys: J/K/L A R    │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ Notes & timecodes for HNG_SH001_LAY_v003                     │
 *   │ ▣ APPROVED   ▣ REVISION   ▣ Internal Feedback                │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Architecture notes:
 *  • Both video elements share a master play/pause/scrub trigger via
 *    `syncedTime`. When the user scrubs either video manually, we sync
 *    the other to the same time (best-effort — different durations are
 *    OK; the CBA is usually shorter than the animator's playblast).
 *  • Shot list comes from the parent's already-loaded submissions
 *    (props.submissions), grouped by parent_code (deduped to one entry
 *    per shot, keyed by latest version's submission).
 *  • The actual per-shot fetch happens on-demand against
 *    /api/reviews/compare/shot to keep the initial render snappy.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft, ChevronRight, Play, Pause, RotateCcw,
  Check, AlertTriangle, MessageSquare, ChevronDown,
  Maximize2, Minimize2, Send, Edit3, Loader2, RefreshCw,
  SkipBack, SkipForward, Rewind, StepBack, StepForward,
} from 'lucide-react';
import AnnotationLayer, { AnnotationToolbar, useAnnotationController } from './AnnotationLayer';
import { useAuth } from '@/contexts/AuthContext';

const SHOT_RE = /\b(?:sh|shot)[\s_-]?(\d{1,4})\b/i;
const shotNumFrom = (v) => {
  const s = String(v?.parent_code || v?.title || v?.filename || '');
  const m = s.match(SHOT_RE);
  return m ? parseInt(m[1], 10) : null;
};

const isVendorRole = (r) => /vendor|freelance/i.test(r || '');

/**
 * Render an actionable "right panel" message when no animation submission
 * exists yet for the selected shot. Founder fix Feb 11 2026: previously
 * the panel showed the misleading "Awaiting animation submission" even
 * when the artist HAD clicked REVIEW and the auto-fetch had FAILED
 * silently (bad folder name on NAS, nomenclature mismatch, bridge offline).
 * Now we surface the actual reason + a Retry button.
 */
function AwaitingAnimationMessage({ diag, api, onRetried, projectId, userRole }) {
  const [retrying, setRetrying] = useState(false);
  const [retryErr, setRetryErr] = useState(null);

  const handleRetry = async () => {
    if (!diag?.task_id || retrying) return;
    setRetrying(true);
    setRetryErr(null);
    try {
      await api.post(`/reviews/auto-fetch-from-nas/${diag.task_id}`);
      onRetried && onRetried();
    } catch (e) {
      setRetryErr(e?.response?.data?.detail || e?.message || 'retry failed');
    } finally {
      setRetrying(false);
    }
  };

  const retryBlock = (label) => (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-2 flex-wrap justify-center">
        <button
          onClick={handleRetry}
          disabled={retrying}
          data-testid="compare-await-retry-btn"
          className="px-3 py-1.5 rounded text-[11px] uppercase tracking-wider font-bold border border-amber-500/60 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50"
        >
          {retrying ? 'Retrying…' : label}
        </button>
        {/* Mass fetch — admin/PM/supervisor only. Lets the producer
            skip clicking Retry N times when many shots are stuck. */}
        <BatchFetchAllButton
          projectId={projectId}
          api={api}
          role={userRole}
          onDispatched={onRetried}
        />
      </div>
      {retryErr && (
        <p className="text-[11px] text-red-400 max-w-sm break-words">
          {retryErr}
        </p>
      )}
    </div>
  );

  if (!diag) {
    return (
      <span data-testid="compare-await-no-task">
        No layout task assigned for this shot yet. Once admin creates the
        task and the artist flips it to <b className="text-amber-300">REVIEW</b>,
        the playblast auto-fetches from NAS and the supervisor review starts.
      </span>
    );
  }
  const status = (diag.auto_fetch_status || '').toLowerCase();
  const artist = diag.artist_name ? (
    <span className="text-zinc-300">
      Assigned to <b className="text-amber-300">@{diag.artist_name}</b>.{' '}
    </span>
  ) : null;
  if (status === 'failed') {
    const errText = (diag.auto_fetch_error || '').toLowerCase();
    const isBridgeOffline = errText.includes('bridge_offline')
      || errText.includes('bridge offline')
      || errText.includes('nas bridge')
      || errText.includes('not connected');
    return (
      <div className="space-y-3 text-left max-w-xl mx-auto"
           data-testid="compare-await-fetch-failed">
        {artist}
        <div className={`rounded border px-3 py-2 ${
          isBridgeOffline
            ? 'border-orange-500/50 bg-orange-500/10'
            : 'border-red-500/40 bg-red-500/10'
        }`}>
          <p className={`text-[10px] uppercase tracking-widest mb-1 ${
            isBridgeOffline ? 'text-orange-400' : 'text-red-400'
          }`}>
            {isBridgeOffline ? 'NAS bridge daemon is OFFLINE' : 'Auto-fetch failed'}
          </p>
          <p className="text-[12px] text-zinc-200 break-words">
            {diag.auto_fetch_error || 'Unknown error from NAS bridge.'}
          </p>
          {isBridgeOffline && (
            <p className="text-[11px] text-orange-200 mt-2 leading-snug">
              <b>Action:</b> Open the QNAP bridge daemon on the studio PC
              (or the NAS host) and confirm it is running and connected.
              On reconnect, the auto-fetch will work without any code
              changes — just hit <b>Retry</b> below.
            </p>
          )}
          {diag.auto_fetch_at && (
            <p className="text-[10px] text-zinc-500 mt-1">
              Last attempt: {new Date(diag.auto_fetch_at).toLocaleString()}
            </p>
          )}
        </div>
        {retryBlock('Retry auto-fetch')}
      </div>
    );
  }
  if (status === 'pending') {
    return (
      <span data-testid="compare-await-fetch-pending">
        {artist}
        Auto-fetch in progress — the NAS bridge is pulling the playblast.
        Refresh in 10-20 seconds.
      </span>
    );
  }
  const taskStatus = (diag.task_status || '').toLowerCase();
  if (taskStatus === 'submitted' || taskStatus === 'in_review'
        || taskStatus === 'review') {
    // Task is already in REVIEW state but auto-fetch never fired
    // (legacy tasks from before auto-fetch existed, or a deleted
    // submission left the task orphaned). The artist can't re-flip
    // the dropdown because it's locked at "in_review" — so the
    // producer needs a one-click recovery right here.
    return (
      <div className="space-y-3 text-left max-w-xl mx-auto"
           data-testid="compare-await-fetch-stalled">
        {artist}
        <p className="text-[12px] text-zinc-300">
          Task is in <b className="text-amber-300">REVIEW</b> but no
          playblast has been pulled from NAS yet. This usually means the
          task was flipped before the auto-fetch flow existed, or a
          previous submission was cleaned up. Click below to dispatch the
          NAS fetch + supervisor review now.
        </p>
        {retryBlock('Fetch playblast from NAS')}
      </div>
    );
  }
  return (
    <span data-testid="compare-await-not-triggered">
      {artist}
      No playblast yet — current task status:{' '}
      <b className="text-zinc-300">{diag.task_status || 'unknown'}</b>.
      The artist needs to flip the task to{' '}
      <b className="text-amber-300">REVIEW</b> in MY TASKS to trigger
      auto-fetch from NAS.
    </span>
  );
}

/**
 * BatchFetchAllButton — admin/PM-only mass trigger for re-running
 * auto-fetch across every stuck task in the project.
 *
 * Founder spec Feb 12 2026: when artists rename misnamed NAS files
 * in bulk (e.g. after the strict nomenclature gate flagged 92 TLM
 * Layout shots), the producer needs a SINGLE click to re-run
 * auto-fetch for every task rather than 92 individual Retry presses.
 *
 * UX:
 *   1. Click → call /reviews/admin/batch-auto-fetch-preview to see
 *      how many tasks are stuck (eligible_total + by_status counts).
 *   2. Confirm dialog shows the breakdown.
 *   3. Confirm → POST /reviews/admin/batch-auto-fetch (fire-and-forget).
 *   4. Toast: "Dispatched N task(s) — refresh inbox in 30-60s".
 *
 * Hidden for non-admin / non-PM / non-supervisor roles.
 */
function BatchFetchAllButton({ projectId, api, role, onDispatched }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);   // {eligible_total, by_status}
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);          // {dispatched}

  // RBAC — hide entirely for artists / freelancers / clients
  const allowedRoles = new Set([
    'master_admin', 'super_admin', 'production_manager',
    'supervisor', 'vfx_supervisor',
  ]);
  if (!projectId || !allowedRoles.has((role || '').toLowerCase())) return null;

  const openPreview = async () => {
    if (busy) return;
    setBusy(true); setErr(''); setDone(null);
    try {
      const r = await api.get(
        `/reviews/admin/batch-auto-fetch-preview?project_id=${encodeURIComponent(projectId)}`
      );
      setPreview(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const confirmDispatch = async () => {
    if (busy) return;
    setBusy(true); setErr('');
    try {
      const r = await api.post('/reviews/admin/batch-auto-fetch', {
        project_id: projectId,
        limit: 500,
      });
      setDone(r.data);
      setPreview(null);
      onDispatched && onDispatched();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Dispatch failed');
    } finally {
      setBusy(false);
    }
  };

  const close = () => { setPreview(null); setErr(''); setDone(null); };

  return (
    <>
      <button
        onClick={openPreview}
        disabled={busy}
        data-testid="compare-batch-fetch-all-btn"
        className="h-8 px-3 rounded border border-amber-500/50 text-amber-200 bg-amber-500/10 hover:bg-amber-500/20 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5 disabled:opacity-50"
        title="Re-trigger auto-fetch for every stuck task in this project (admin/PM only)"
      >
        {busy
          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : <RefreshCw className="w-3.5 h-3.5" />}
        Fetch All Missing
      </button>

      {/* Preview / confirm dialog */}
      {(preview || done || err) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={close}
          data-testid="compare-batch-fetch-modal"
        >
          <div
            className="bg-zinc-950 border border-zinc-800 rounded-lg p-5 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-[14px] font-bold text-zinc-100 mb-3 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-amber-400" />
              {done ? 'Auto-fetch dispatched' : 'Mass re-fetch eligible tasks'}
            </h3>

            {done && (
              <div className="space-y-2 text-[12px] text-zinc-300"
                   data-testid="compare-batch-fetch-done">
                <p className="text-emerald-300 font-semibold">
                  ✓ Dispatched {done.dispatched} task(s) in the background.
                </p>
                <p className="text-zinc-400 text-[11px] leading-snug">
                  Each task will update its own status chip on the My
                  Tasks row / Approvals Inbox in the next 30-60 seconds.
                  Refresh the page to see the new results.
                </p>
              </div>
            )}

            {preview && !done && (
              <div className="space-y-3 text-[12px]"
                   data-testid="compare-batch-fetch-preview">
                <p className="text-zinc-300">
                  Found <b className="text-amber-200">{preview.eligible_total}</b> task(s)
                  eligible for auto-fetch in this project.
                </p>
                {preview.eligible_total > 0 ? (
                  <>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 rounded bg-zinc-900 border border-zinc-800">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Previously failed</div>
                        <div className="text-[18px] font-bold text-rose-300">{preview.by_status?.failed || 0}</div>
                      </div>
                      <div className="p-2 rounded bg-zinc-900 border border-zinc-800">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">In progress</div>
                        <div className="text-[18px] font-bold text-sky-300">{preview.by_status?.pending || 0}</div>
                      </div>
                      <div className="p-2 rounded bg-zinc-900 border border-zinc-800">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Never tried</div>
                        <div className="text-[18px] font-bold text-zinc-300">{preview.by_status?.never || 0}</div>
                      </div>
                    </div>
                    <p className="text-[11px] text-zinc-500 leading-snug">
                      The bridge will scan NAS for each task and only
                      create a submission if a canonically-named playblast
                      is found. Tasks with mis-named files will surface a
                      &ldquo;Nomenclature mismatch&rdquo; error in their auto-fetch
                      chip — rename on NAS and try again.
                    </p>
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        onClick={confirmDispatch}
                        disabled={busy}
                        data-testid="compare-batch-fetch-confirm-btn"
                        className="flex-1 h-9 rounded bg-amber-500 hover:bg-amber-400 text-zinc-950 text-[11px] uppercase tracking-wider font-bold disabled:opacity-50 flex items-center justify-center gap-1.5"
                      >
                        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        Dispatch fetch for {preview.eligible_total} task(s)
                      </button>
                      <button
                        onClick={close}
                        className="h-9 px-3 rounded border border-zinc-700 text-zinc-300 text-[11px] uppercase tracking-wider hover:bg-zinc-900"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="text-[11px] text-emerald-300">
                    No stuck tasks — every eligible task in this project
                    already has a successful auto-fetch. ✓
                  </p>
                )}
              </div>
            )}

            {err && (
              <div className="mt-3 p-2 rounded bg-rose-500/10 border border-rose-500/40 text-[11px] text-rose-200"
                   data-testid="compare-batch-fetch-err">
                {err}
              </div>
            )}

            {!preview && !done && !err && (
              <p className="text-[11px] text-zinc-500">Loading…</p>
            )}

            <button
              onClick={close}
              className="mt-3 w-full h-8 rounded border border-zinc-800 text-zinc-500 text-[10px] uppercase tracking-wider hover:bg-zinc-900"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}


export default function CompareView({ submissions, artistsByDept, projectId, dept, onDecide, mediaUrlFor }) {
  const { api, user } = useAuth();
  const userRole = user?.role || '';

  // ── Group submissions by SHOT (parent_code) — one row per shot ──
  // Artist + shot dropdowns work off this structure.
  const shotsByArtist = useMemo(() => {
    const map = new Map();  // submitter_id → Map(shot_num → {shot_num, parent_code, submissions[]})
    (submissions || []).forEach(s => {
      const aid = s.submitter_id || s.created_by || 'unknown';
      if (!map.has(aid)) map.set(aid, new Map());
      const shotMap = map.get(aid);
      const num = shotNumFrom(s);
      // Use parent_code when available, else fall back to title; some
      // older clubbed uploads have only the title with embedded numbers.
      const key = num ?? `t:${s.parent_code || s.title || s.id}`;
      if (!shotMap.has(key)) {
        shotMap.set(key, {
          shot_num: num,
          parent_code: s.parent_code || s.title,
          submissions: [],
        });
      }
      shotMap.get(key).submissions.push(s);
    });
    // Normalize → object keyed by artist id with shot list sorted
    const out = {};
    for (const [aid, shotMap] of map.entries()) {
      const shots = Array.from(shotMap.values()).sort(
        (a, b) => (a.shot_num ?? 9999) - (b.shot_num ?? 9999));
      out[aid] = shots;
    }
    return out;
  }, [submissions]);

  // ── Artist selection (defaults to first artist with submissions) ──
  const allArtists = useMemo(() => {
    const arr = [];
    Object.values(artistsByDept || {}).forEach(list => arr.push(...(list || [])));
    // De-dupe by submitter_id
    const seen = new Set();
    return arr.filter(a => {
      if (seen.has(a.submitter_id)) return false;
      seen.add(a.submitter_id);
      return shotsByArtist[a.submitter_id]?.length > 0;
    });
  }, [artistsByDept, shotsByArtist]);

  const [selectedArtist, setSelectedArtist] = useState(null);
  useEffect(() => {
    if (!selectedArtist && allArtists.length > 0) {
      setSelectedArtist(allArtists[0]);
    }
  }, [allArtists, selectedArtist]);

  const artistShots = (selectedArtist && shotsByArtist[selectedArtist.submitter_id]) || [];

  // ── CBA-fallback shot list (Feb 2026 founder ask: "CBA clip MUST
  // AUTO FILL ONCE IN the Folder") ────────────────────────────────
  // When there's NO animation submission yet, the LEFT panel must
  // still surface every CBA clip for the project so producers can
  // preview shots that are awaiting an artist's REVIEW dropdown
  // trigger. We fetch /reviews/compare/cba-shots once on mount and
  // treat the result as the shot list when `allArtists` is empty.
  const [cbaShots, setCbaShots] = useState([]);
  const usingCbaFallback = (allArtists.length === 0
                              && cbaShots.length > 0);
  useEffect(() => {
    if (!projectId) { setCbaShots([]); return; }
    let cancelled = false;
    api.get(`/reviews/compare/cba-shots?project_id=${projectId}`)
      .then(r => {
        if (cancelled) return;
        setCbaShots(r.data?.shots || []);
      })
      .catch(() => { if (!cancelled) setCbaShots([]); });
    return () => { cancelled = true; };
  }, [api, projectId]);

  // The actual shot list used by the navigator: artist's submissions
  // when present, otherwise the CBA fallback.
  const effectiveShots = artistShots.length > 0 ? artistShots : cbaShots;

  // ── Shot selection ──────────────────────────────────────────────
  const [shotIdx, setShotIdx] = useState(0);
  useEffect(() => { setShotIdx(0); }, [selectedArtist]);
  // Clamp shotIdx in render — handles the source-list switch
  // (artistShots ↔ cbaShots) without an extra set-state effect.
  const safeShotIdx = Math.min(shotIdx, Math.max(0, effectiveShots.length - 1));
  const currentShot = effectiveShots[safeShotIdx] || null;

  // ── Adjacent-shot prefetch (Feb 9, 2026 founder ask: "please
  // speed up the whole process") ────────────────────────────────
  // When the user lands on shot N, silently prefetch shots N-1 and
  // N+1 so clicking next/prev is instant. Uses sessionStorage to
  // dedupe repeat prefetches across re-renders within the same tab.
  useEffect(() => {
    if (!currentShot || !projectId || effectiveShots.length < 2) return;
    const neighbours = [];
    if (safeShotIdx > 0) neighbours.push(effectiveShots[safeShotIdx - 1]);
    if (safeShotIdx < effectiveShots.length - 1) neighbours.push(effectiveShots[safeShotIdx + 1]);

    const cancelled = { v: false };
    neighbours.forEach(shot => {
      const sc = shot.parent_code
        || shot.shot_code
        || (shot.shot_num != null ? `SH_${String(shot.shot_num).padStart(3, '0')}` : '');
      if (!sc) return;
      const dedupeKey = `cba_prefetch_v1_${projectId}_${sc}`;
      try {
        if (typeof window !== 'undefined' && window.sessionStorage?.getItem(dedupeKey)) return;
      } catch (_e) { /* sessionStorage may be unavailable */ }

      // Fire-and-forget: hit compare/shot to warm the API cache, then
      // hit cba-stream to materialise + cache the bytes in cloud.
      api.get(`/reviews/compare/shot?project_id=${projectId}&shot_code=${encodeURIComponent(sc)}`)
        .then(r => {
          if (cancelled.v) return;
          const cbaUrl = r.data?.cba_clip?.video_url || '';
          if (!cbaUrl.startsWith('/api/reviews/cba-stream/')) return;
          // Materialise (cheap if already cached) — HEAD-style probe
          // with responseType:'blob' fires the lazy pipeline; the
          // browser caches the response so the user's later click
          // is instant.
          const rel = cbaUrl.replace(/^\/api/, '');
          return api.get(rel, { responseType: 'blob' });
        })
        .then(() => {
          try { window.sessionStorage?.setItem(dedupeKey, '1'); } catch (_e) { /* noop */ }
        })
        .catch(() => { /* swallow — prefetch is best-effort */ });
    });

    return () => { cancelled.v = true; };
  }, [api, projectId, currentShot, safeShotIdx, effectiveShots]);

  // ── Project-level warm-cache trigger (one shot per project per
  // tab session). Kicks off the bounded-parallel background job that
  // materialises every uncached CBA clip for the project. After
  // warm-up, every shot click is instant from cloud cache. ─────────
  useEffect(() => {
    if (!projectId) return;
    const dedupeKey = `cba_warm_v1_${projectId}`;
    try {
      if (typeof window !== 'undefined' && window.sessionStorage?.getItem(dedupeKey)) return;
      window.sessionStorage?.setItem(dedupeKey, '1');
    } catch (_e) { /* noop */ }
    api.post(`/reviews/cba/warm-cache?project_id=${projectId}`)
      .catch(() => { /* best-effort */ });
  }, [api, projectId]);

  // ── Fetch CBA + versions for the current shot ──────────────────
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  // Bumped after a successful manual auto-fetch retry so the inbox
  // re-fetches the shot and picks up the freshly-attached playblast
  // (or a fresh failure reason) without a page reload.
  const [refreshKey, setRefreshKey] = useState(0);

  // ── NAS bridge health pill ──────────────────────────────────────
  // Founder spec Feb 11 2026: when a bridge dies, every auto-fetch
  // returns `bridge_offline`. The producer used to think it was a
  // code bug — now we show a global red pill in the inbox header so
  // they immediately escalate to "restart QNAP bridge daemon" rather
  // than asking the dev. Poll every 30s, plus an immediate refresh
  // when the producer hits the Retry button (so the pill goes green
  // the moment the bridge comes back).
  const [bridgeStatus, setBridgeStatus] = useState({
    connected: true, checking: true,
  });
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get('/nas/status');
        if (cancelled) return;
        setBridgeStatus({
          connected: !!r.data?.bridge_connected,
          checking: false,
          fresh_agents: (r.data?.fresh_agents || []).length,
        });
      } catch (_e) {
        if (!cancelled) setBridgeStatus({ connected: false, checking: false });
      }
    };
    tick();
    const iv = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [api, refreshKey]);

  useEffect(() => {
    if (!currentShot || !projectId) { setCompareData(null); return; }
    const shotCode = currentShot.parent_code
      || (currentShot.shot_num != null ? `SH_${String(currentShot.shot_num).padStart(3, '0')}` : '');
    if (!shotCode) { setCompareData(null); return; }
    setLoading(true);
    let cancelled = false;
    api.get(`/reviews/compare/shot?project_id=${projectId}&shot_code=${encodeURIComponent(shotCode)}`)
      .then(r => {
        if (cancelled) return;
        setCompareData(r.data || null);
        setSelectedVersion(r.data?.latest || null);
      })
      .catch(() => { if (!cancelled) setCompareData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, projectId, currentShot, refreshKey]);

  // ── Resolve playable URLs (auth-aware blob fetch) ──────────────
  const [cbaSrc, setCbaSrc] = useState('');
  const [cbaError, setCbaError] = useState('');
  const [animSrc, setAnimSrc] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCbaError('');
      const cbaUrl = compareData?.cba_clip?.video_url
        ? { url: compareData.cba_clip.video_url, storage_key: null }
        : null;
      if (!cbaUrl) { setCbaSrc(''); return; }
      const u = await mediaUrlFor(cbaUrl);
      if (cancelled) return;
      setCbaSrc(u);
      // Founder UX (Feb 8 2026): when the cba-stream lazy materializer
      // can't resolve a NAS path (no nas_mapping, bridge offline,
      // label mismatch), the resolver swallows the error and falls
      // through to the direct URL — which 401s on the <video> tag and
      // shows a silent black panel. Probe the URL once after resolution
      // so we can surface the backend's actionable detail in the UI.
      const rawUrl = cbaUrl.url || '';
      if (rawUrl.startsWith('/api/reviews/cba-stream/') && !u.startsWith('blob:')) {
        try {
          const rel = rawUrl.replace(/^\/api/, '');
          await api.get(rel, { responseType: 'blob' });
        } catch (e) {
          if (cancelled) return;
          let detail = e?.response?.data;
          if (detail instanceof Blob) {
            try { detail = JSON.parse(await detail.text()); } catch (_e) { detail = null; }
          }
          const msg = (detail && typeof detail === 'object' && detail.detail)
            || e?.message
            || 'Unable to fetch CBA clip from NAS.';
          setCbaError(String(msg));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [api, compareData, mediaUrlFor]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const m = selectedVersion?.media_url
        ? { url: selectedVersion.media_url, storage_key: null }
        : null;
      if (m) {
        const u = await mediaUrlFor(m);
        if (!cancelled) setAnimSrc(u);
      } else { setAnimSrc(''); }
    })();
    return () => { cancelled = true; };
  }, [selectedVersion, mediaUrlFor]);

  // ── Synced playback controls ───────────────────────────────────
  const cbaRef = useRef(null);
  const animRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const masterPlay = useCallback(() => {
    setPlaying(true);
    cbaRef.current?.play().catch(() => {});
    animRef.current?.play().catch(() => {});
  }, []);
  const masterPause = useCallback(() => {
    setPlaying(false);
    cbaRef.current?.pause();
    animRef.current?.pause();
  }, []);
  const masterTogglePlay = useCallback(() => {
    if (playing) masterPause(); else masterPlay();
  }, [playing, masterPlay, masterPause]);
  const syncToZero = useCallback(() => {
    if (cbaRef.current) cbaRef.current.currentTime = 0;
    if (animRef.current) animRef.current.currentTime = 0;
  }, []);

  // Sync scrub: when ONE video seeks, mirror to the other.
  const handleSeek = useCallback((source) => () => {
    const src = source === 'cba' ? cbaRef.current : animRef.current;
    const dst = source === 'cba' ? animRef.current : cbaRef.current;
    if (src && dst && Math.abs(src.currentTime - dst.currentTime) > 0.15) {
      dst.currentTime = src.currentTime;
    }
  }, []);

  // ── Timeline state (SyncSketch-style — May 23 2026) ──────────────
  // Track currentTime + duration of the "active" video (animator's
  // playblast when present, else CBA). The Timeline component reads
  // these to draw the playhead, frame ticks, and comment markers.
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [fps, setFps] = useState(24); // common animation FPS

  useEffect(() => {
    const v = animRef.current || cbaRef.current;
    if (!v) return;
    const onTime = () => setCurrentTime(v.currentTime || 0);
    const onMeta = () => setDuration(v.duration || 0);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('durationchange', onMeta);
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('durationchange', onMeta);
    };
  }, [animSrc, cbaSrc]);

  const seekBoth = useCallback((t) => {
    const clamped = Math.max(0, Math.min(t, duration || t));
    if (cbaRef.current) cbaRef.current.currentTime = clamped;
    if (animRef.current) animRef.current.currentTime = clamped;
    setCurrentTime(clamped);
  }, [duration]);

  // ── Notes panel state (timecode-anchored — May 23 2026) ──────────
  // Loaded per-version. The endpoint /reviews/{id}/comments returns
  // {id, text, frame, created_at, author_name}. We also keep a draft
  // for the "Add note at frame X" composer.
  const [notes, setNotes] = useState([]);
  const [draftNote, setDraftNote] = useState('');
  const loadNotes = useCallback(async () => {
    if (!selectedVersion?.id) { setNotes([]); return; }
    try {
      const r = await api.get(`/reviews/${selectedVersion.id}/comments`);
      const items = (r.data?.items || r.data?.comments || r.data || []);
      setNotes(Array.isArray(items) ? items : []);
    } catch (_e) {
      setNotes([]);
    }
  }, [api, selectedVersion]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const addNote = useCallback(async () => {
    if (!draftNote.trim() || !selectedVersion?.id) return;
    const frame = Math.round(currentTime * fps);
    try {
      // Backend endpoint is /reviews/{id}/comment (singular). Accepts
      // {note, frame, timecode} — frame is the optional anchor for the
      // Compare View timeline marker.
      await api.post(`/reviews/${selectedVersion.id}/comment`, {
        note: draftNote.trim(),
        frame,
        timecode: currentTime,
      });
      setDraftNote('');
      loadNotes();
    } catch (_e) { /* surface via parent if needed */ }
  }, [api, draftNote, selectedVersion, currentTime, fps, loadNotes]);

  // ── Annotation mode (May 25, 2026 v2 — SyncSketch parity) ─────────
  // AUTO-PIN model: every completed stroke fires `persistOneStroke`
  // immediately, so the artist never loses work. Toolbar lives in
  // the player header (NOT overlapping the art). ESC exits annotate
  // mode.
  const [annoMode, setAnnoMode] = useState(null);  // 'cba' | 'anim' | null
  const persistOneStroke = useCallback(async (stroke) => {
    if (!selectedVersion?.id) return;
    const frame = Math.round(currentTime * fps);
    if (stroke?.delete) {
      // Eraser deleted an existing stroke — for now we log a delete
      // marker; the comment-row will be reconciled by the supervisor's
      // "clear all" action below. (Per-stroke deletion of historical
      // marks is a Phase-3 polish item.)
      return;
    }
    const annotated = { ...stroke, anchor: annoMode || 'anim' };
    try {
      await api.post(`/reviews/${selectedVersion.id}/comment`, {
        note: `[Annotation · ${stroke.tool} @ f${String(frame).padStart(4, '0')}]`,
        frame,
        timecode: currentTime,
        annotations: [annotated],
      });
      loadNotes();
    } catch (_e) { /* optimistic stroke remains visible on the SVG */ }
  }, [api, selectedVersion, currentTime, fps, annoMode, loadNotes]);

  // "Clear all marks on this frame" — wipes every annotation row anchored
  // to the current frame. Backend has no bulk-delete endpoint yet, so
  // we soft-clear by posting a special "[Annotations cleared]" comment
  // and the renderer respects that as a frame-reset.
  // TODO Phase 3: DELETE /api/reviews/comments/{id}
  const clearAllForFrame = useCallback(async () => {
    if (!selectedVersion?.id) return;
    const frame = Math.round(currentTime * fps);
    try {
      await api.post(`/reviews/${selectedVersion.id}/comment`, {
        note: `[Annotations cleared at f${String(frame).padStart(4, '0')}]`,
        frame,
        timecode: currentTime,
        annotations: [{ tool: 'clear_marker', color: '#000', width: 0, points: [], anchor: annoMode || 'anim' }],
      });
      loadNotes();
    } catch (_e) { /* */ }
  }, [api, selectedVersion, currentTime, fps, annoMode, loadNotes]);

  // Auto-pause when annotation mode turns on.
  useEffect(() => {
    if (!annoMode) return;
    if (cbaRef.current && !cbaRef.current.paused) cbaRef.current.pause();
    if (animRef.current && !animRef.current.paused) animRef.current.pause();
  }, [annoMode]);

  // Strokes for the CURRENT frame, partitioned by pane. A "clear_marker"
  // resets that pane's display until a newer annotation comes in
  // (founder ask: clear button must visually wipe the frame).
  const currentFrame = Math.round(currentTime * fps);
  const frameAnnotations = useMemo(() => {
    const byPane = { cba: [], anim: [] };
    const sorted = [...notes].sort((a, b) => (a.at || '').localeCompare(b.at || ''));
    sorted.forEach(n => {
      if (!Array.isArray(n.annotations) || !n.annotations.length) return;
      if (n.frame !== currentFrame) return;
      n.annotations.forEach(a => {
        const pane = a.anchor === 'cba' ? 'cba' : 'anim';
        if (a.tool === 'clear_marker') {
          byPane[pane] = [];   // reset everything posted before this clear
        } else {
          byPane[pane].push(a);
        }
      });
    });
    return byPane;
  }, [notes, currentFrame]);

  // Annotation controllers — one per pane. The toolbar reads/writes
  // tool/color/width state via the controller; the layer renders.
  const cbaCtrl = useAnnotationController({
    active: annoMode === 'cba',
    currentFrame,
    existingStrokes: frameAnnotations.cba,
    onPersistStroke: persistOneStroke,
    onClearAllForFrame: clearAllForFrame,
  });
  const animCtrl = useAnnotationController({
    active: annoMode === 'anim',
    currentFrame,
    existingStrokes: frameAnnotations.anim,
    onPersistStroke: persistOneStroke,
    onClearAllForFrame: clearAllForFrame,
  });

  // ── Maximize / fullscreen toggle ──────────────────────────────────
  const rootRef = useRef(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const toggleMaximize = useCallback(() => {
    setIsMaximized(v => !v);
  }, []);

  // ── Shot navigation ───────────────────────────────────────────
  const goPrev = useCallback(() => {
    setShotIdx(i => Math.max(0, i - 1));
  }, []);
  const goNext = useCallback(() => {
    setShotIdx(i => Math.min(effectiveShots.length - 1, i + 1));
  }, [effectiveShots.length]);

  // ── Decide actions ────────────────────────────────────────────
  const [busyDecision, setBusyDecision] = useState('');
  const doDecide = useCallback(async (decision) => {
    if (!selectedVersion?.id) return;
    setBusyDecision(decision);
    try {
      await onDecide(selectedVersion.id, decision);
      // Refresh the shot data
      const shotCode = compareData?.shot_code;
      if (shotCode) {
        const r = await api.get(`/reviews/compare/shot?project_id=${projectId}&shot_code=${encodeURIComponent(shotCode)}`);
        setCompareData(r.data || null);
        setSelectedVersion(r.data?.latest || null);
      }
    } catch (_e) { /* parent already toasts */ } finally {
      setBusyDecision('');
    }
  }, [api, compareData, onDecide, projectId, selectedVersion]);

  // ── Hot keys: J/K/L shuttle, , / . frame step, ←/→ jump shots,
  // A approve, R revise, F fullscreen. Frame-step pauses both videos
  // first (SyncSketch convention — you never frame-step while playing).
  useEffect(() => {
    const stepFrames = (dir) => {
      masterPause();
      const dt = dir / (fps || 24);
      seekBoth((currentTime || 0) + dt);
    };
    const onKey = (e) => {
      // Don't fire on inputs/textareas
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA'
          || e.target.isContentEditable) return;
      switch (e.key) {
        case ',': stepFrames(-1); break;
        case '.': stepFrames(+1); break;
        case '<': stepFrames(-10); break;  // Shift+, — 10-frame jog
        case '>': stepFrames(+10); break;  // Shift+. — 10-frame jog
        default: {
          switch (e.key.toLowerCase()) {
            case 'j': {
              // Rewind 1s on both
              if (cbaRef.current) cbaRef.current.currentTime = Math.max(0, cbaRef.current.currentTime - 1);
              if (animRef.current) animRef.current.currentTime = Math.max(0, animRef.current.currentTime - 1);
              break;
            }
            case 'k': masterTogglePlay(); break;
            case 'l': {
              if (cbaRef.current) cbaRef.current.currentTime += 1;
              if (animRef.current) animRef.current.currentTime += 1;
              break;
            }
            case 'arrowleft': goPrev(); break;
            case 'arrowright': goNext(); break;
            case 'a': doDecide('approved'); break;
            case 'r': doDecide('revise'); break;
            case 'f': toggleMaximize(); break;
            default: return;
          }
        }
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [masterTogglePlay, masterPause, goPrev, goNext, doDecide,
      toggleMaximize, seekBoth, currentTime, fps]);

  // ── Always render the two-pane shell ─────────────────────────────────
  // Founder spec (May 23 2026): "even if there are no shots, that
  // beautiful panel must be visible". So we no longer early-return on
  // empty data — the artist picker, shot picker, both player frames,
  // and the decision bar all render with friendly empty-state copy
  // inside them. Conveys WHAT the screen does even before there's
  // anything to review.
  // Founder spec (Feb 2026): "CBA clip MUST AUTO FILL ONCE IN the
  // Folder". When there are no submissions yet but the project has CBA
  // clips ingested, drive the shot picker from the CBA list so producers
  // can preview the reference cuts before any artist submits. The truly
  // empty state only renders when both submissions AND CBA clips are zero.
  const isEmpty = allArtists.length === 0 && cbaShots.length === 0;

  return (
    <div
      ref={rootRef}
      className={isMaximized
        ? 'fixed inset-0 z-50 bg-zinc-950 p-4 overflow-y-auto space-y-3'
        : 'space-y-3'}
      data-testid="compare-view"
    >
      {/* ── Header: Artist + Shot selectors + Maximize + Prev/Next ────── */}
      <div className="flex items-center justify-between flex-wrap gap-3 bg-zinc-950 border border-zinc-800 rounded p-3">
        <div className="flex items-center gap-2 flex-wrap">
          {isEmpty ? (
            <p className="text-[11px] text-zinc-500 italic">No CBA clips ingested yet for this project — once the animatics artist uploads the cut, shots will appear here automatically.</p>
          ) : usingCbaFallback ? (
            <>
              <span
                className="text-[10px] uppercase tracking-widest text-amber-400/80"
                data-testid="compare-cba-fallback-banner"
              >Awaiting Animation</span>
              <span className="text-zinc-700">·</span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Shot</span>
              <ShotPicker
                shots={effectiveShots}
                value={safeShotIdx}
                onChange={setShotIdx}
              />
              {/* Artist label from auto_fetch_diag (founder fix Feb 11
                  2026): even when no submission exists yet, surface
                  WHO is assigned to the shot so the producer knows
                  who to chase. */}
              {compareData?.auto_fetch_diag?.artist_name && (
                <>
                  <span className="text-zinc-700">·</span>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">Artist</span>
                  <span
                    className="text-[11px] font-mono text-amber-300 px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/30"
                    data-testid="compare-await-artist-name"
                  >@{compareData.auto_fetch_diag.artist_name}</span>
                </>
              )}
              {/* NAS bridge health pill — green when daemon online,
                  red when offline. Telegraphs the actual infra state
                  so producers stop assuming code bugs. */}
              <span className="text-zinc-700">·</span>
              <span
                className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${
                  bridgeStatus.checking
                    ? 'text-zinc-500 border-zinc-700 bg-zinc-800/40'
                    : bridgeStatus.connected
                      ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
                      : 'text-red-300 border-red-500/50 bg-red-500/10'
                }`}
                title={bridgeStatus.connected
                  ? `NAS bridge live (${bridgeStatus.fresh_agents || 0} agent${(bridgeStatus.fresh_agents || 0) === 1 ? '' : 's'})`
                  : 'NAS bridge daemon is OFFLINE — restart it on the studio PC'}
                data-testid="compare-bridge-status-pill"
              >
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${
                  bridgeStatus.checking ? 'bg-zinc-500'
                    : bridgeStatus.connected ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'
                }`} />
                Bridge {bridgeStatus.checking ? '…' : bridgeStatus.connected ? 'Live' : 'Offline'}
              </span>
              <span className="text-[10px] text-zinc-500 ml-2 hidden md:inline">
                Artist will trigger the playblast by setting status to <span className="text-amber-400">REVIEW</span> in MY TASKS.
              </span>
            </>
          ) : (
            <>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Artist</span>
              <ArtistPicker
                allArtists={allArtists}
                value={selectedArtist}
                onChange={setSelectedArtist}
                shotsByArtist={shotsByArtist}
              />
              {currentShot && (
                <>
                  <span className="text-zinc-700">·</span>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">Shot</span>
                  <ShotPicker
                    shots={artistShots}
                    value={safeShotIdx}
                    onChange={setShotIdx}
                  />
                </>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Founder spec Feb 12 2026 — mass re-fetch for admin/PM/sup
              when artists have renamed NAS files in bulk. Hidden for
              artists / freelancers / clients via internal RBAC. */}
          <BatchFetchAllButton
            projectId={projectId}
            api={api}
            role={userRole}
            onDispatched={() => setRefreshKey(k => k + 1)}
          />
          <button onClick={goPrev} disabled={isEmpty || safeShotIdx === 0} data-testid="compare-prev-shot"
                  className="w-8 h-8 rounded border border-zinc-700 hover:bg-zinc-800 disabled:opacity-40 flex items-center justify-center">
            <ChevronLeft className="w-4 h-4 text-zinc-300" />
          </button>
          <span className="text-[11px] text-zinc-500 font-mono">
            {effectiveShots.length === 0 ? '—' : `${safeShotIdx + 1} / ${effectiveShots.length}`}
          </span>
          <button onClick={goNext} disabled={isEmpty || safeShotIdx >= effectiveShots.length - 1} data-testid="compare-next-shot"
                  className="w-8 h-8 rounded border border-zinc-700 hover:bg-zinc-800 disabled:opacity-40 flex items-center justify-center">
            <ChevronRight className="w-4 h-4 text-zinc-300" />
          </button>
          <button onClick={toggleMaximize}
                  data-testid="compare-maximize"
                  title={isMaximized ? 'Exit fullscreen (F)' : 'Maximize for closer inspection (F)'}
                  className="w-8 h-8 rounded border border-zinc-700 hover:bg-zinc-800 flex items-center justify-center">
            {isMaximized
              ? <Minimize2 className="w-4 h-4 text-zinc-300" />
              : <Maximize2 className="w-4 h-4 text-zinc-300" />}
          </button>
        </div>
      </div>

      {/* ── Main grid: TWO PANES + RIGHT NOTES RAIL ───────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_1fr_300px] gap-3">
        {/* LEFT — CBA / Reference */}
        <PlayerPanel
          title="Reference (CBA)"
          subtitle={compareData?.cba_clip?.canonical_name || (isEmpty ? 'CBA reference goes here' : 'No CBA clip linked')}
          videoRef={cbaRef}
          src={cbaError ? '' : cbaSrc}
          missing={!compareData?.cba_clip || !!cbaError}
          missingMsg={cbaError
            ? `Unable to fetch CBA from NAS: ${cbaError}`
            : (isEmpty
              ? 'Once allotted, the Cut-By-Animatics reference clip plays here so producers can compare the rough cut with the animator\'s playblast side-by-side.'
              : 'No reference clip — link a CBA clip in Allotment')}
          onSeek={handleSeek('cba')}
          accent="zinc"
          loading={loading}
          dataTestid="compare-cba"
          annoMode={annoMode === 'cba'}
          toggleAnno={() => setAnnoMode(m => m === 'cba' ? null : 'cba')}
          ctrl={cbaCtrl}
          onAnnoExit={() => setAnnoMode(null)}
        />
        {/* RIGHT — Animator's playblast (with version dropdown) */}
        <PlayerPanel
          title="Animator Playblast"
          subtitle={selectedVersion?.canonical_name
            || (usingCbaFallback ? 'Awaiting animation submission' : (isEmpty ? 'Latest animation playblast' : 'No submission yet'))}
          videoRef={animRef}
          src={animSrc}
          missing={!selectedVersion}
          missingMsg={usingCbaFallback
            ? <AwaitingAnimationMessage
                diag={compareData?.auto_fetch_diag}
                api={api}
                onRetried={() => setRefreshKey(k => k + 1)}
                projectId={projectId}
                userRole={userRole}
              />
            : (isEmpty
                ? 'When an animator submits via MY TASKS, the latest version plays here. Use the version dropdown to compare v1 vs v3, hot keys J/K/L to shuttle, A/R to approve or revise.'
                : 'Awaiting first submission for this shot')}
          onSeek={handleSeek('anim')}
          accent="amber"
          loading={loading}
          dataTestid="compare-anim"
          annoMode={annoMode === 'anim'}
          toggleAnno={() => setAnnoMode(m => m === 'anim' ? null : 'anim')}
          ctrl={animCtrl}
          onAnnoExit={() => setAnnoMode(null)}
          versionPicker={
            compareData?.versions?.length > 1 && (
              <VersionPicker
                versions={compareData.versions}
                value={selectedVersion}
                onChange={setSelectedVersion}
              />
            )
          }
        />

        {/* RIGHT RAIL — Notes panel (timecode-anchored).
            Hidden on screens < xl so the two-pane stays usable;
            visible on wider monitors which is where Compare gets used. */}
        <NotesPanel
          notes={notes}
          draft={draftNote}
          onDraftChange={setDraftNote}
          onAddNote={addNote}
          onJumpToFrame={(f) => seekBoth((f || 0) / fps)}
          currentTime={currentTime}
          fps={fps}
          disabled={!selectedVersion}
          emptyHint={isEmpty
            ? 'Producer notes appear here. Pause at any frame and write a note — it pins to that exact timecode.'
            : 'No notes yet. Pause the video at any frame and type a note below — it will pin to the current timecode.'}
        />
      </div>

      {/* ── Timeline (SyncSketch-style — May 23 2026) ───────────────
          Playhead, frame ticks, comment markers, FPS picker. Visible
          even when empty so the founder sees the structure. Clicking
          anywhere seeks BOTH videos to that timecode. */}
      <Timeline
        currentTime={currentTime}
        duration={duration}
        fps={fps}
        notes={notes}
        playing={playing}
        onSeek={seekBoth}
        onTogglePlay={masterTogglePlay}
        onChangeFps={setFps}
        onStepFrame={(delta) => seekBoth((currentTime || 0) + (delta / fps))}
        onSkip={(deltaSec) => seekBoth(Math.max(0, (currentTime || 0) + deltaSec))}
        onRewindToStart={() => { masterPause(); seekBoth(0); }}
        onFullscreen={toggleMaximize}
        isFullscreen={isMaximized}
      />

      {/* ── Master controls + Decision bar ────────────────────── */}
      <div className="bg-zinc-950 border border-zinc-800 rounded p-3 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-1">
          <button onClick={masterTogglePlay} data-testid="compare-master-play"
                  className="h-9 px-3 rounded border border-zinc-700 hover:bg-zinc-800 flex items-center gap-1.5">
            {playing ? <Pause className="w-4 h-4 text-zinc-200" /> : <Play className="w-4 h-4 text-emerald-400" />}
            <span className="text-[11px] uppercase tracking-wider text-zinc-300">{playing ? 'Pause Both' : 'Play Both'}</span>
          </button>
          <button onClick={syncToZero} title="Sync both videos to start"
                  data-testid="compare-sync-zero"
                  className="h-9 w-9 rounded border border-zinc-700 hover:bg-zinc-800 flex items-center justify-center">
            <RotateCcw className="w-4 h-4 text-zinc-300" />
          </button>
          <span className="text-[10px] text-emerald-300 ml-2 hidden md:inline bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1">
            Keys: <span className="font-mono text-emerald-200">J</span> rewind ·{' '}
            <span className="font-mono text-emerald-200">K</span> play/pause ·{' '}
            <span className="font-mono text-emerald-200">L</span> forward ·{' '}
            <span className="font-mono text-emerald-200">,</span>/<span className="font-mono text-emerald-200">.</span> frame ·{' '}
            <span className="font-mono text-emerald-200">&lt;</span>/<span className="font-mono text-emerald-200">&gt;</span> ±10f ·{' '}
            <span className="font-mono text-emerald-200">A</span> approve ·{' '}
            <span className="font-mono text-emerald-200">R</span> revise ·{' '}
            <span className="font-mono text-emerald-200">F</span> fullscreen ·{' '}
            <span className="font-mono text-emerald-200">←/→</span> shots
          </span>
        </div>
        <div className="flex items-center gap-2">
          <DecisionPill
            active={selectedVersion?.decision === 'approved'}
            color="emerald"
            label="Approved"
            icon={Check}
            busy={busyDecision === 'approved'}
            disabled={!selectedVersion}
            onClick={() => doDecide('approved')}
            testid="compare-approve"
          />
          <DecisionPill
            active={selectedVersion?.decision === 'revise' || selectedVersion?.decision === 'changes_requested'}
            color="rose"
            label="Revision"
            icon={AlertTriangle}
            busy={busyDecision === 'revise'}
            disabled={!selectedVersion}
            onClick={() => doDecide('revise')}
            testid="compare-revise"
          />
        </div>
      </div>

      {/* ── Footer info row ───────────────────────────────────── */}
      {selectedVersion && (
        <div className="text-[11px] text-zinc-500 flex items-center gap-3 px-1">
          <span>By @{selectedVersion.submitter_handle || selectedVersion.submitter_name}</span>
          <span>·</span>
          <span>{compareData?.version_count} version{compareData?.version_count !== 1 ? 's' : ''}</span>
          {selectedVersion.notes_count > 0 && (
            <>
              <span>·</span>
              <span className="flex items-center gap-1">
                <MessageSquare className="w-3 h-3" />{selectedVersion.notes_count} note{selectedVersion.notes_count !== 1 ? 's' : ''}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── Sub-components ────────────────────────────

function ArtistPicker({ allArtists, value, onChange, shotsByArtist }) {
  const [open, setOpen] = useState(false);
  // Founder fix May 25, 2026: dropdown didn't close on outside click —
  // wrap in a ref, attach mousedown listener that closes when click is
  // outside the dropdown root.
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const internal = allArtists.filter(a => !isVendorRole(a.role));
  const vendors = allArtists.filter(a => isVendorRole(a.role));
  return (
    <div className="relative" ref={rootRef}>
      <button onClick={() => setOpen(o => !o)} data-testid="compare-artist-picker"
              className="h-8 px-3 rounded border border-zinc-700 hover:bg-zinc-800 text-[12px] text-zinc-200 flex items-center gap-1.5">
        <span className={`h-1.5 w-1.5 rounded-full ${isVendorRole(value?.role) ? 'bg-cyan-400' : 'bg-emerald-400'}`} />
        @{value?.handle || value?.name || 'Pick artist'}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-30 bg-zinc-900 border border-zinc-700 rounded shadow-xl min-w-[240px] max-h-80 overflow-y-auto py-1">
          {internal.map(a => (
            <ArtistRow key={a.submitter_id} artist={a} count={shotsByArtist[a.submitter_id]?.length} onClick={() => { onChange(a); setOpen(false); }} selected={a.submitter_id === value?.submitter_id} />
          ))}
          {internal.length > 0 && vendors.length > 0 && (
            <div className="text-[9px] uppercase tracking-widest text-zinc-600 px-3 py-1 border-t border-zinc-800 mt-1">Vendors</div>
          )}
          {vendors.map(a => (
            <ArtistRow key={a.submitter_id} artist={a} count={shotsByArtist[a.submitter_id]?.length} onClick={() => { onChange(a); setOpen(false); }} selected={a.submitter_id === value?.submitter_id} />
          ))}
        </div>
      )}
    </div>
  );
}

function ArtistRow({ artist, count, onClick, selected }) {
  const vendor = isVendorRole(artist.role);
  return (
    <button onClick={onClick}
            data-testid={`compare-artist-row-${artist.submitter_id}`}
            className={`w-full flex items-center justify-between px-3 py-1.5 text-left text-[12px] ${selected ? 'bg-amber-500/15 text-amber-200' : 'hover:bg-zinc-800 text-zinc-200'}`}>
      <span className="flex items-center gap-1.5 min-w-0">
        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${vendor ? 'bg-cyan-400' : 'bg-emerald-400'}`} />
        <span className="truncate">@{artist.handle || artist.name}</span>
      </span>
      <span className="text-[10px] px-1.5 py-0.5 bg-zinc-800 text-zinc-300 rounded font-mono ml-2 shrink-0">{count || 0}</span>
    </button>
  );
}

function ShotPicker({ shots, value, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const current = shots[value];
  const label = current?.parent_code
    || current?.shot_code
    || (current?.shot_num != null ? `SH_${String(current.shot_num).padStart(3, '0')}` : '—');
  return (
    <div className="relative" ref={rootRef}>
      <button onClick={() => setOpen(o => !o)} data-testid="compare-shot-picker"
              className="h-8 px-3 rounded border border-zinc-700 hover:bg-zinc-800 text-[12px] text-zinc-200 flex items-center gap-1.5 font-mono">
        {label}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-30 bg-zinc-900 border border-zinc-700 rounded shadow-xl min-w-[180px] max-h-80 overflow-y-auto py-1">
          {shots.map((s, i) => {
            const lbl = s.parent_code
              || s.shot_code
              || (s.shot_num != null ? `SH_${String(s.shot_num).padStart(3, '0')}` : `#${i + 1}`);
            const versionCount = Array.isArray(s.submissions) ? s.submissions.length : 0;
            const badge = versionCount > 0
              ? `${versionCount}v`
              : (s.cba_clip_id ? 'CBA' : '—');
            return (
              <button key={i} onClick={() => { onChange(i); setOpen(false); }}
                      data-testid={`compare-shot-row-${i}`}
                      className={`w-full flex items-center justify-between px-3 py-1.5 text-left text-[12px] font-mono ${i === value ? 'bg-amber-500/15 text-amber-200' : 'hover:bg-zinc-800 text-zinc-200'}`}>
                <span>{lbl}</span>
                <span className={`text-[9px] ${versionCount > 0 ? 'text-zinc-500' : 'text-amber-500/70'}`}>{badge}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VersionPicker({ versions, value, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div className="relative" ref={rootRef}>
      <button onClick={() => setOpen(o => !o)} data-testid="compare-version-picker"
              className="h-7 px-2 rounded border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15 text-[10px] uppercase tracking-wider text-amber-200 flex items-center gap-1">
        v{String(value?.version || 0).padStart(3, '0')}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 z-30 bg-zinc-900 border border-zinc-700 rounded shadow-xl min-w-[200px] max-h-72 overflow-y-auto py-1">
          {[...versions].reverse().map(v => (
            <button key={v.id} onClick={() => { onChange(v); setOpen(false); }}
                    data-testid={`compare-version-row-${v.version}`}
                    className={`w-full flex items-center justify-between px-3 py-1.5 text-left text-[11px] ${v.id === value?.id ? 'bg-amber-500/15 text-amber-200' : 'hover:bg-zinc-800 text-zinc-200'}`}>
              <span className="font-mono">v{String(v.version).padStart(3, '0')}</span>
              <span className={`text-[9px] uppercase tracking-wider ${v.decision === 'approved' ? 'text-emerald-400' : v.decision ? 'text-rose-400' : 'text-zinc-500'}`}>
                {v.decision || 'pending'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PlayerPanel({
  title, subtitle, videoRef, src, missing, missingMsg, onSeek, accent, loading, dataTestid, versionPicker,
  annoMode = false, toggleAnno, ctrl, onAnnoExit,
}) {
  return (
    <div className={`border rounded overflow-hidden bg-zinc-950 self-start ${accent === 'amber' ? 'border-amber-500/40' : 'border-zinc-800'}`} data-testid={dataTestid}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="text-[9px] uppercase tracking-widest text-zinc-500">{title}</p>
          <p className="text-[12px] font-mono text-zinc-200 truncate" data-testid={`${dataTestid}-subtitle`}>{subtitle}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Annotation toolbar lives in the HEADER (founder fix May 25 2026):
              previously it floated on top of the video, blocking the art. */}
          {annoMode && <AnnotationToolbar ctrl={ctrl} onExit={onAnnoExit} />}
          {!missing && src && toggleAnno && !annoMode && (
            <button
              onClick={toggleAnno}
              data-testid={`${dataTestid}-anno-toggle`}
              title="Annotate frame"
              className="h-7 px-2 rounded text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-amber-500/60"
            >
              <Edit3 className="w-3 h-3" /> Annotate
            </button>
          )}
          {versionPicker}
        </div>
      </div>
      {missing ? (
        <div className="aspect-video flex items-center justify-center text-zinc-500 text-[12px] text-center p-6" data-testid={`${dataTestid}-missing`}>
          {loading ? 'Loading…' : missingMsg}
        </div>
      ) : src ? (
        <div className="relative w-full bg-black rounded overflow-hidden">
          {/* Founder bug Feb 2026 (vawworld.in /reviews → Approvals →
              Layout & Animation): two video issues — (a) browser's
              native <video controls> rendered ON TOP of our custom
              playback bar (J/K/L shuttle, frame counter, FPS picker,
              decision pills) creating a confusing dual-control UI;
              (b) the panel forced `aspect-video` 16:9 so when the
              source video was portrait or shorter than 16:9, an
              enormous black void appeared below the actual frame
              and made the page feel "clumsy".
              Fix: drop `controls` (custom bar IS the control surface);
              drop `aspect-video` (let the video render at its native
              aspect with `object-contain`); cap height at 70vh so a
              very-tall portrait clip can't push the decision pills
              off-screen. `block` removes the 4px inline-vertical-
              align gap below the video element. `disablePictureInPicture`
              + `controlsList="nodownload"` are defensive guards in case
              a future React re-render adds controls back. */}
          <video
            ref={videoRef}
            src={src}
            onSeeked={onSeek}
            playsInline
            preload="metadata"
            disablePictureInPicture
            controlsList="nodownload"
            data-testid={`${dataTestid}-video`}
            className="w-full max-h-[70vh] object-contain block mx-auto"
          />
          <AnnotationLayer ctrl={{ ...ctrl, onExit: onAnnoExit }} />
        </div>
      ) : (
        <div className="aspect-video flex items-center justify-center text-zinc-600 text-[11px]">Loading…</div>
      )}
    </div>
  );
}

function DecisionPill({ active, color, label, icon: Icon, busy, disabled, onClick, testid }) {
  // Founder fix May 25, 2026: resting Approved/Revision buttons had a
  // tinted background (bg-emerald-500/15 on hover) that made them look
  // "already selected" to first-time users. Now: NEUTRAL resting state
  // (just a thin border + tinted text), HARD-SOLID active state when
  // clicked. Hover reveals a subtle tint as an affordance.
  const colorClass = color === 'emerald'
    ? (active
        ? 'bg-emerald-500 text-zinc-950 border-emerald-400 shadow-lg shadow-emerald-500/30'
        : 'border-zinc-700 text-zinc-300 hover:border-emerald-500/60 hover:text-emerald-300 hover:bg-emerald-500/5')
    : (active
        ? 'bg-rose-500 text-zinc-950 border-rose-400 shadow-lg shadow-rose-500/30'
        : 'border-zinc-700 text-zinc-300 hover:border-rose-500/60 hover:text-rose-300 hover:bg-rose-500/5');
  return (
    <button onClick={onClick} disabled={disabled || busy} data-testid={testid}
            className={`h-9 px-4 rounded border text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-all ${colorClass}`}>
      <Icon className="w-4 h-4" />
      {busy ? '…' : label}
    </button>
  );
}

// ─── TIMELINE (SyncSketch-style — May 23 2026) ─────────────────────
// Playhead bar with frame ticks, comment markers, FPS picker.
// Click anywhere to seek both videos. Width-responsive.

function Timeline({
  currentTime, duration, fps, notes, playing,
  onSeek, onTogglePlay, onChangeFps,
  // Founder spec Jun 24 2026 — full transport bar. These are
  // optional so existing callers that didn't wire them up don't
  // crash; they degrade gracefully (button disabled).
  onStepFrame,      // (delta: int) → seek ± delta frames
  onSkip,           // (delta_sec: float) → seek ± delta seconds
  onRewindToStart,  // → seek to 0 and pause
  onFullscreen,     // → toggle fullscreen on the player container
  isFullscreen,
}) {
  const totalFrames = Math.max(1, Math.round((duration || 0) * fps));
  const currentFrame = Math.round((currentTime || 0) * fps);

  const onTimelineClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    onSeek(pct * (duration || 0));
  };

  // Decide tick density — show every frame if total <= 100, else
  // step so the timeline stays uncluttered.
  const tickStep = totalFrames <= 80 ? 1
    : totalFrames <= 200 ? 5
    : totalFrames <= 600 ? 10
    : 25;
  const ticks = [];
  for (let f = 0; f <= totalFrames; f += tickStep) ticks.push(f);

  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid="compare-timeline">
      {/* Founder spec Jun 24 2026 — full transport bar.
          Before: just a tiny play icon + frame counter + FPS dropdown.
          After:  Rewind to 0 │ -10f │ -1f │ -5s │ ▶/⏸ │ +5s │ +1f │ +10f │ Fullscreen
                  + frame counter + seconds + FPS. */}
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-1">
          {/* Transport cluster */}
          <button onClick={onRewindToStart}
                  disabled={!onRewindToStart}
                  title="Rewind to start (Home)"
                  data-testid="compare-timeline-rewind-start"
                  className="w-8 h-8 rounded border border-zinc-700 hover:bg-zinc-800 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
            <Rewind className="w-3.5 h-3.5 text-zinc-300" />
          </button>
          <button onClick={() => onStepFrame && onStepFrame(-10)}
                  disabled={!onStepFrame}
                  title="−10 frames (<)"
                  data-testid="compare-timeline-back-10f"
                  className="w-8 h-8 rounded border border-zinc-700 hover:bg-zinc-800 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
            <SkipBack className="w-3.5 h-3.5 text-zinc-300" />
          </button>
          <button onClick={() => onStepFrame && onStepFrame(-1)}
                  disabled={!onStepFrame}
                  title="−1 frame (,)"
                  data-testid="compare-timeline-back-1f"
                  className="w-8 h-8 rounded border border-zinc-700 hover:bg-zinc-800 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
            <StepBack className="w-3.5 h-3.5 text-zinc-300" />
          </button>
          <button onClick={() => onSkip && onSkip(-5)}
                  disabled={!onSkip}
                  title="−5 seconds (J)"
                  data-testid="compare-timeline-back-5s"
                  className="w-8 h-8 rounded border border-zinc-700 hover:bg-zinc-800 flex items-center justify-center text-[10px] font-mono text-zinc-300 disabled:opacity-40 disabled:cursor-not-allowed">
            −5s
          </button>
          <button onClick={onTogglePlay}
                  data-testid="compare-timeline-play"
                  title="Play / Pause (K or Space)"
                  className="w-10 h-8 rounded border border-emerald-600/40 bg-emerald-500/10 hover:bg-emerald-500/20 flex items-center justify-center">
            {playing ? <Pause className="w-4 h-4 text-zinc-100" />
                     : <Play className="w-4 h-4 text-emerald-400" />}
          </button>
          <button onClick={() => onSkip && onSkip(5)}
                  disabled={!onSkip}
                  title="+5 seconds (L)"
                  data-testid="compare-timeline-fwd-5s"
                  className="w-8 h-8 rounded border border-zinc-700 hover:bg-zinc-800 flex items-center justify-center text-[10px] font-mono text-zinc-300 disabled:opacity-40 disabled:cursor-not-allowed">
            +5s
          </button>
          <button onClick={() => onStepFrame && onStepFrame(1)}
                  disabled={!onStepFrame}
                  title="+1 frame (.)"
                  data-testid="compare-timeline-fwd-1f"
                  className="w-8 h-8 rounded border border-zinc-700 hover:bg-zinc-800 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
            <StepForward className="w-3.5 h-3.5 text-zinc-300" />
          </button>
          <button onClick={() => onStepFrame && onStepFrame(10)}
                  disabled={!onStepFrame}
                  title="+10 frames (>)"
                  data-testid="compare-timeline-fwd-10f"
                  className="w-8 h-8 rounded border border-zinc-700 hover:bg-zinc-800 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
            <SkipForward className="w-3.5 h-3.5 text-zinc-300" />
          </button>
          {onFullscreen && (
            <button onClick={onFullscreen}
                    title="Fullscreen (F)"
                    data-testid="compare-timeline-fullscreen"
                    className="w-8 h-8 rounded border border-zinc-700 hover:bg-zinc-800 flex items-center justify-center ml-2">
              {isFullscreen
                ? <Minimize2 className="w-3.5 h-3.5 text-zinc-300" />
                : <Maximize2 className="w-3.5 h-3.5 text-zinc-300" />}
            </button>
          )}
          {/* Frame counter */}
          <div className="text-[10px] font-mono text-zinc-300 ml-3" data-testid="compare-timeline-frame">
            <span className="text-amber-200">{currentFrame.toString().padStart(4, '0')}</span>
            <span className="text-zinc-600"> / {totalFrames.toString().padStart(4, '0')}</span>
            <span className="text-zinc-600 ml-1">f</span>
          </div>
          <span className="text-zinc-700">·</span>
          <div className="text-[10px] font-mono text-zinc-500">
            {(currentTime || 0).toFixed(2)}s / {(duration || 0).toFixed(2)}s
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">FPS</label>
          <select
            value={fps}
            onChange={e => onChangeFps(parseInt(e.target.value, 10))}
            data-testid="compare-timeline-fps"
            className="h-7 px-2 text-[11px] rounded bg-zinc-900 border border-zinc-700 text-zinc-200 font-mono"
          >
            {[12, 18, 24, 25, 29.97, 30, 50, 60].map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Track */}
      <div
        onClick={onTimelineClick}
        className="relative h-9 bg-zinc-900 rounded border border-zinc-800 cursor-pointer overflow-hidden select-none"
        data-testid="compare-timeline-track"
      >
        {/* Frame ticks */}
        {ticks.map(f => {
          const pct = totalFrames > 0 ? (f / totalFrames) * 100 : 0;
          const major = f % (tickStep * 5) === 0;
          return (
            <div key={f} className="absolute top-0 bottom-0" style={{ left: `${pct}%` }}>
              <div className={major ? 'w-px h-3 bg-zinc-600' : 'w-px h-2 bg-zinc-700'} />
              {major && (
                <span className="absolute top-3 -translate-x-1/2 text-[8px] text-zinc-500 font-mono whitespace-nowrap">
                  {f}
                </span>
              )}
            </div>
          );
        })}

        {/* Comment markers */}
        {(notes || []).map(n => {
          const frame = n.frame ?? (n.timecode != null ? Math.round(n.timecode * fps) : null);
          if (frame == null || totalFrames === 0) return null;
          const pct = (frame / totalFrames) * 100;
          return (
            <div
              key={n.id || frame}
              title={`Frame ${frame} — ${n.text || n.author_name || ''}`}
              className="absolute top-0 bottom-0 w-0.5 bg-amber-300 hover:bg-amber-200 cursor-pointer"
              style={{ left: `${pct}%` }}
              data-testid={`compare-timeline-note-${n.id || frame}`}
            >
              <div className="absolute -top-1 -left-1 w-2 h-2 rounded-full bg-amber-300" />
            </div>
          );
        })}

        {/* Playhead */}
        {totalFrames > 0 && (
          <div
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{ left: `${(currentFrame / totalFrames) * 100}%` }}
            data-testid="compare-timeline-playhead"
          >
            <div className="w-0.5 h-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.6)]" />
            <div className="absolute -top-1 -translate-x-1/2 w-2.5 h-2.5 bg-rose-500 rotate-45" />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── NOTES PANEL (timecode-anchored — May 23 2026) ─────────────────
// Producers pause at a frame, type a note, it pins to that timecode.
// Click any note to seek back to its frame on both videos.

function NotesPanel({ notes, draft, onDraftChange, onAddNote, onJumpToFrame, currentTime, fps, disabled, emptyHint }) {
  const currentFrame = Math.round((currentTime || 0) * fps);
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded flex flex-col min-h-[360px]" data-testid="compare-notes-panel">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
        <h3 className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold flex items-center gap-1.5">
          <MessageSquare className="w-3 h-3" /> Notes
        </h3>
        <span className="text-[9px] text-zinc-600 font-mono">{notes.length}</span>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5" data-testid="compare-notes-list">
        {notes.length === 0 ? (
          <p className="text-[10px] text-zinc-500 italic px-1 py-2">{emptyHint}</p>
        ) : (
          notes
            .slice()
            .sort((a, b) => (a.frame ?? 0) - (b.frame ?? 0))
            .map(n => {
              const frame = n.frame ?? Math.round((n.timecode || 0) * fps);
              return (
                <button
                  key={n.id || frame}
                  onClick={() => onJumpToFrame(frame)}
                  data-testid={`compare-note-${n.id || frame}`}
                  className="w-full text-left bg-zinc-900 border border-zinc-800 rounded p-2 hover:border-amber-500/40 group"
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[9px] font-mono text-amber-300">f{String(frame).padStart(4, '0')}</span>
                    <span className="text-[9px] text-zinc-500 truncate ml-2">@{n.author_name || n.author_handle || '—'}</span>
                  </div>
                  <p className="text-[11px] text-zinc-200 group-hover:text-white whitespace-pre-wrap break-words">{n.text}</p>
                </button>
              );
            })
        )}
      </div>
      <div className="border-t border-zinc-800 p-2 space-y-1.5">
        <div className="flex items-center gap-1.5 text-[9px] text-zinc-500 font-mono">
          <span className="text-amber-300">f{String(currentFrame).padStart(4, '0')}</span>
          <span className="text-zinc-700">·</span>
          <span>{(currentTime || 0).toFixed(2)}s</span>
          <span className="text-zinc-700 ml-auto uppercase tracking-widest">Anchored at playhead</span>
        </div>
        <textarea
          value={draft}
          onChange={e => onDraftChange(e.target.value)}
          disabled={disabled}
          rows={2}
          placeholder={disabled ? 'Open a submission first' : 'Write a note for this frame…'}
          data-testid="compare-note-draft"
          className="w-full text-[11px] bg-zinc-900 border border-zinc-800 rounded p-2 text-zinc-200 placeholder:text-zinc-600 disabled:opacity-50 resize-none focus:outline-none focus:border-amber-500/40"
        />
        <button
          onClick={onAddNote}
          disabled={disabled || !draft.trim()}
          data-testid="compare-note-submit"
          className="w-full h-7 rounded bg-amber-500 hover:bg-amber-400 text-zinc-950 text-[10px] uppercase tracking-wider font-bold flex items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Send className="w-3 h-3" /> Add note at f{String(currentFrame).padStart(4, '0')}
        </button>
      </div>
    </div>
  );
}

